import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { CalendarRange, Plus, Trash2, Calendar } from 'lucide-react';

const PAGES = ['Somesh Aditya', 'Stories by SHINE', 'SHINE Ad Agency', 'Vizag Diaries', 'SHINE Reels', 'Beach City Buzz'];
const STATUSES = ['Draft', 'Scheduled', 'Posted', 'Cancelled'];
const TYPES = ['Reel', 'Post', 'Story', 'Carousel', 'Video', 'Article'];
const STATUS_COLOR = {
  Draft: 'border-slate-500/40 text-slate-300',
  Scheduled: 'border-sky-500/40 text-sky-300',
  Posted: 'border-emerald-500/40 text-emerald-300',
  Cancelled: 'border-red-500/40 text-red-300',
};

const DEFAULT_CHECKLIST = ['Caption finalized', 'Visual approved', 'Scheduled', 'Posted + engaged first hour'];

export default function ContentHubPage() {
  const { isManager, user } = useAuth();
  const canDelete = isManager || user?.role === 'Content Writer/Creator';
  const [items, setItems] = useState([]);
  const [pageFilter, setPageFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ page: PAGES[0], date: '', content_type: 'Post', title: '', caption: '', status: 'Draft' });

  const load = async () => {
    const res = await api.get('/content' + (pageFilter !== 'all' ? `?page=${encodeURIComponent(pageFilter)}` : ''));
    setItems(res.data);
  };
  useEffect(() => { load(); }, [pageFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm({ page: PAGES[0], date: new Date().toISOString().slice(0, 10), content_type: 'Post', title: '', caption: '', status: 'Draft' });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...it, date: it.date?.slice(0, 10) });
    setOpen(true);
  };

  const save = async () => {
    const payload = { ...form };
    if (!editing) {
      payload.checklist = DEFAULT_CHECKLIST.map((t) => ({ id: crypto.randomUUID(), text: t, done: false }));
      await api.post('/content', payload);
    } else {
      await api.patch(`/content/${editing.id}`, payload);
    }
    setOpen(false); load();
  };

  const toggleChecklist = async (it, item, done) => {
    const checklist = it.checklist.map((c) => c.id === item.id ? { ...c, done } : c);
    await api.patch(`/content/${it.id}`, { checklist });
    load();
  };

  const remove = async (it) => {
    if (!confirm(`Delete "${it.title}"?`)) return;
    await api.delete(`/content/${it.id}`); load();
  };

  const changeStatus = async (it, status) => {
    await api.patch(`/content/${it.id}`, { status }); load();
  };

  return (
    <div>
      <PageHeader
        icon={CalendarRange}
        title="Content Hub"
        subtitle="Plan and post for SHINE-owned pages."
        actions={(
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold" data-testid="new-content-btn"><Plus className="h-4 w-4 mr-1" /> New entry</Button></DialogTrigger>
            <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-lg">
              <DialogHeader><DialogTitle className="text-white">{editing ? 'Edit entry' : 'New calendar entry'}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Page</Label>
                    <Select value={form.page} onValueChange={(v) => setForm({ ...form, page: v })}>
                      <SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0a1628] border-amber-500/20">{PAGES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type</Label>
                    <Select value={form.content_type} onValueChange={(v) => setForm({ ...form, content_type: v })}>
                      <SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0a1628] border-amber-500/20">{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0a1628] border-amber-500/20">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
                <div><Label>Caption</Label><Textarea rows={4} value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
              </div>
              <DialogFooter><Button onClick={save} disabled={!form.title || !form.date} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      <Card className="shine-card p-3 rounded-xl mb-4 flex flex-wrap items-center gap-3">
        <Label className="text-xs text-slate-400">Page:</Label>
        <Select value={pageFilter} onValueChange={setPageFilter}>
          <SelectTrigger className="w-56 h-9 bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a1628] border-amber-500/20">
            <SelectItem value="all">All pages</SelectItem>
            {PAGES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      {items.length === 0 ? (
        <Card className="shine-card p-10 text-center text-slate-400 rounded-xl">No content entries yet.</Card>
      ) : (
        <div className="grid lg:grid-cols-2 gap-3">
          {items.map((it) => (
            <Card key={it.id} className="shine-card p-4 rounded-xl" data-testid={`content-card-${it.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="border-amber-500/40 text-amber-200 text-[10px]">{it.page}</Badge>
                    <Badge variant="outline" className="border-sky-500/40 text-sky-300 text-[10px]">{it.content_type}</Badge>
                  </div>
                  <h3 className="font-bold text-white">{it.title}</h3>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">{it.caption}</p>
                  <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(it.date).toLocaleDateString()}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Select value={it.status} onValueChange={(v) => changeStatus(it, v)}>
                    <SelectTrigger className={`h-8 text-xs ${STATUS_COLOR[it.status]} bg-[#0f2240] border-amber-500/20 w-[120px]`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a1628] border-amber-500/20">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex gap-0.5">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(it)} className="text-amber-300 hover:bg-amber-500/10 h-7 w-7"><Calendar className="h-3.5 w-3.5" /></Button>
                    {canDelete && <Button size="icon" variant="ghost" onClick={() => remove(it)} className="text-red-400 hover:bg-red-500/10 h-7 w-7"><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </div>
                </div>
              </div>
              {(it.checklist || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-amber-500/10 space-y-1.5">
                  {it.checklist.map((c) => (
                    <div key={c.id} className="flex items-center gap-2">
                      <Checkbox checked={!!c.done} onCheckedChange={(v) => toggleChecklist(it, c, !!v)} className="border-amber-500/40 data-[state=checked]:bg-amber-500" />
                      <span className={`text-xs ${c.done ? 'line-through text-slate-500' : 'text-slate-200'}`}>{c.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
